import orderBy from "lodash/orderBy";
import find from "lodash/find";
import isString from "lodash/isString";
import get from "lodash/get";
import Vue from "vue"
import moment from "moment"
import { v4 as uuidv4 } from 'uuid';
import { EventBus } from "@/services/event-bus"

// app-specific localStorage keys that should be cleared when switching stores
export const APP_SPECIFIC_KEYS = [
  'vuex', 'lastStoreId', 'forcePopup', 'forcePopupReferral', 
  'anonymousLikes', 'lang', 'guestOrderHistory', 'featureState'
];

export const slugifyMenuItemName = (name) => {
  if (!isString(name)) return ''
  return name
    .toLowerCase()
    .normalize('NFD')   
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export const resolveMenuItemId = (idParam, catParam, categories) => {
  const scoped = catParam
    ? categories.filter(c => slugifyMenuItemName(c.name) === catParam)
    : categories

  for (const category of scoped) {
    for (const item of (category.items || [])) {
      if (item.code === idParam) return idParam
    }
  }
  for (const category of scoped) {
    for (const item of (category.items || [])) {
      if (slugifyMenuItemName(item.name) === idParam) return item.code
    }
  }
  return idParam
}

export const generateMenuAnchorId = (menuName) => {
  const text = isString(menuName) ? menuName : ''
  // Anchor IDs MUST start with an ALPHA character. It cannot start with a number
  const prefix = 'MENU_'
  // encoded to support characters outside the Latin1 set, which btoa only supports
  const hash = btoa(unescape(encodeURIComponent(text)))
    .replace(/\+/g, '-') // replace all "+"
    .replace(/\//g, '--') // replace all "/"
    .replace(/=/g, '---') // replace all "="

  return `${prefix}${hash}`
}

export const calculateDistance = (lat1, lon1, lat2, lon2, unit = 'K') => {
  if ((lat1 == lat2) && (lon1 == lon2)) {
    return 0;
  }
  else {
    const radlat1 = Math.PI * lat1/180;
    const radlat2 = Math.PI * lat2/180;
    const theta = lon1-lon2;
    const radtheta = Math.PI * theta/180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = dist * 180/Math.PI;
    dist = dist * 60 * 1.1515;
    if (unit === 'K') { dist = dist * 1.609344 }
    if (unit === 'N') { dist = dist * 0.8684 }
    return dist;
  }
}

export const revertLangCode = (code) => {
  if (code == 'zhHans')
    return 'zh'
  if (code == 'zhTW')
    return 'zh-TW'
  return code
}
// Maps vuetify lang codes to BCP 47 locale strings for Intl date/time formatting
export const getDisplayLocale = (vuetifyLangCode) => {
  const map = {
    en: 'en-US',
    zhHans: 'zh-CN',
    zhTW: 'zh-TW',
    fr: 'fr-FR',
    es: 'es-ES',
    ar: 'ar-SA',
    ko: 'ko-KR',
    th: 'th-TH',
    vi: 'vi-VN',
  }
  return map[vuetifyLangCode] || 'en-US'
}

export const convertLangCode = (code) => {
  if (code == 'zh')
    return 'zhHans'
  if (code == 'zh-TW')
    return 'zhTW'
  return code
}

export const getTimeFraction = (hour, minute) => {
  return Math.round((hour + minute / 60) * 100) / 100
}

export const convertTimeFraction = (fraction) => {
  const hours = Math.floor(fraction)
  const minutes = Math.round((Number(fraction)-hours) * 60)
  return { hours, minutes }
}

export const revertTimeFraction = (fraction) => {
  const dt = new Date()

  if (fraction !== null) {
    const hours = Math.floor(fraction); // extract the hours (in 24 hour format)

    const min = 1 / 60;
    // Round to nearest minute
    const decpart = min * Math.round((fraction - hours) / min);
    const minutes = Math.floor(decpart * 60) + '';

    dt.setHours(hours)
    dt.setMinutes(minutes)
    dt.setSeconds(0)
  }

  return dt
}

export const humanReadableTimeFraction = (fraction) => {
  return formatTime(revertTimeFraction(fraction))
}

// It's common for us to include attributes that are 'x minutes to do this'
// Telling the user 100 minutes is poor usability, so we break it up into hours, minutes
export const humanReadableMinutes = (mins) => {
  const _t = Vue.prototype.$vuetify.t

  if (mins >= 60) {
    const hours = Math.floor(mins/60)
    mins = mins%60

    if (mins > 0) {
      return `${hours} ${_t('$vuetify.cart.estTimeHours')}, ${mins} ${_t('$vuetify.cart.estTimeMins')}`
    } else {
      return `${hours} ${_t('$vuetify.cart.estTimeHours')}`
    }
  }

  return `${mins} ${_t('$vuetify.cart.estTimeMins')}`
}

// Converts a UTC ISO string to an ISO string whose local time matches the
// store's wall-clock time. This mirrors what moment did:
//   moment.tz(date, storeTimezone).tz(localTZ, true)
// toLocaleString gives us the wall-clock digits in store TZ; parsing them
// without a TZ suffix creates a Date in local time with those same digits.
export const toStoreLocalISO = (dateStr, storeTimezone) => {
  if (!dateStr || !storeTimezone) return dateStr
  try {
    const wallClock = new Date(dateStr).toLocaleString('en-US', {
      timeZone: storeTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      // hour12: false is not enough on its own -- some ICU/Node builds still
      // emit "24" for midnight instead of "00" (Invalid Date once parsed).
      // hourCycle: 'h23' pins the 00-23 range regardless of ICU version.
      hourCycle: 'h23'
    })
    // wallClock is e.g. "06/23/2026, 14:30:00" — parse as local time
    const [datePart, timePart] = wallClock.split(', ')
    const [month, day, year] = datePart.split('/')
    return new Date(`${year}-${month}-${day}T${timePart}`).toISOString()
  } catch (e) {
    return dateStr
  }
}

// Returns a Date whose getHours()/getMinutes()/getDay() etc. reflect the
// store's wall-clock time right now, regardless of which timezone the
// customer's browser is in. Same toLocaleString technique as toStoreLocalISO
// above, just anchored on "now" instead of a given date string. This is what
// every open/closed hours check must read from — never a bare `new Date()`,
// which reports the CUSTOMER's local time and silently miscomputes store
// hours for anyone not in the same timezone as the store (in either
// direction — ahead or behind).
export const nowInStoreTz = (storeTimezone) => {
  if (!storeTimezone) return new Date()
  try {
    const wallClock = new Date().toLocaleString('en-US', {
      timeZone: storeTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      // hour12: false is not enough on its own -- some ICU/Node builds still
      // emit "24" for midnight instead of "00" (Invalid Date once parsed).
      // hourCycle: 'h23' pins the 00-23 range regardless of ICU version.
      hourCycle: 'h23'
    })
    const [datePart, timePart] = wallClock.split(', ')
    const [month, day, year] = datePart.split('/')
    return new Date(`${year}-${month}-${day}T${timePart}`)
  } catch (e) {
    return new Date()
  }
}

// Converts a scheduled ISO time into the store-local decimal-hour fraction
// used by validTimeSlots (e.g. 12.0166666 = 12:01 AM). Anchors on the
// scheduled date's own wall clock in the store's timezone, same technique
// as nowInStoreTz/toStoreLocalISO above.
const scheduledTimeToStoreFraction = (scheduledTime, storeTimezone) => {
  const wallClock = new Date(scheduledTime).toLocaleString('en-US', {
    timeZone: storeTimezone || undefined,
    // hourCycle: 'h23' -- see nowInStoreTz's comment on why hour12: false alone isn't safe.
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  })
  const [hourStr, minStr] = wallClock.split(':')
  return getTimeFraction(Number(hourStr), Number(minStr))
}

// A scheduled time is stale once it falls outside every feasible range on
// its OWN day (matched by dateOffset, not hardcoded to "today" -- a time
// scheduled for tomorrow must be checked against tomorrow's slots, never
// today's, or every future-day booking past today's closing time would
// wrongly show as stale). A scheduled date beyond every offset day_slots
// has (i.e. entirely outside the store's booking window) also counts as
// stale -- the backend simply has nothing to offer there.
//
// isGroupOrder controls how scheduledTime is read, matching _formatScheduledTime
// (CheckoutPage.vue) exactly: group-order times are real UTC instants from the
// server and need the timeZone conversion below. Solo scheduledTime is a wall-clock
// value already expressed in the store's local time (built from getStoreAvailableOrderDays'
// own local Date objects), so converting it AGAIN via storeTimezone double-shifts it --
// read its hours/minutes directly instead, same as formatTime(raw) does elsewhere.
export const isTimeStale = (validTimeSlots, scheduledTime, storeTimezone, isGroupOrder = false) => {
  if (!validTimeSlots || !scheduledTime || scheduledTime === 'now') return false
  const daySlots = validTimeSlots.day_slots || []
  const dt = new Date(scheduledTime)
  const nowLocal = nowInStoreTz(storeTimezone)
  const startOfToday = new Date(nowLocal.getFullYear(), nowLocal.getMonth(), nowLocal.getDate())
  const startOfScheduled = isGroupOrder
    ? (() => {
        const wallClock = dt.toLocaleString('en-US', { timeZone: storeTimezone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' })
        const [month, day, year] = wallClock.split('/')
        return new Date(Number(year), Number(month) - 1, Number(day))
      })()
    : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
  const dateOffset = Math.round((startOfScheduled - startOfToday) / (24 * 60 * 60 * 1000))
  const matchingDay = daySlots.find(d => d.dateOffset === dateOffset)
  if (!matchingDay) return true
  const scheduledFraction = isGroupOrder
    ? scheduledTimeToStoreFraction(scheduledTime, storeTimezone)
    : getTimeFraction(dt.getHours(), dt.getMinutes())
  const fits = (matchingDay.time_slots || []).some(s => scheduledFraction >= s.start && scheduledFraction <= s.end)
  return !fits
}

export const formatDate = (date, locale = 'en-US') => {
  date = ensureDateObject(date)
  if (date) {
    return date.toLocaleDateString(locale, { month: 'short', day: '2-digit' })
  }
  return "N/A"
};

export const formatTime = (time) => {
    time = ensureDateObject(time)

    const convert24To12 = (num) => {
      if (num == 0)
        return 12
      return num >= 13 ? num-12 : num
    }

    const zeroPadding = (num, digit) => {
      let zero = '';
      for (let i = 0; i < digit; i++)
        zero += '0';
      return (zero + num).slice(-digit);
    }

    if (time) {
      return `${convert24To12(time.getHours())}:${zeroPadding(time.getMinutes(), 2)}${time.getHours() < 12 ? 'am' : 'pm'}`
    }
};

export const formateDateTime = (date) => {
  return `${formatDate(date)}, ${formatTime(date)}`
}

export const compareDates = (a,b) => {
  // Return number of hours between two dates
  return Math.floor(((ensureDateObject(b) - ensureDateObject(a))/1000)/60/60);
}

export const getStoreHoursMassaged = (store) => {
  const { hours = [] } = store

  const dayMap = {
    1: `${Vue.prototype.$vuetify.t('$vuetify.hours.sun')}`,
    2: `${Vue.prototype.$vuetify.t('$vuetify.hours.mon')}`,
    3: `${Vue.prototype.$vuetify.t('$vuetify.hours.tues')}`,
    4: `${Vue.prototype.$vuetify.t('$vuetify.hours.wed')}`,
    5: `${Vue.prototype.$vuetify.t('$vuetify.hours.thurs')}`,
    6: `${Vue.prototype.$vuetify.t('$vuetify.hours.fri')}`,
    7: `${Vue.prototype.$vuetify.t('$vuetify.hours.sat')}`
  }

  const times = []
  for (const dayKey in dayMap) {
    // dayKey is a string, even though its defined as a int in the map.
    const config = hours.find((h) => h.day === parseInt(dayKey, 10));

    const day = dayMap[ dayKey ]
    let open, close

    if (config) {
      let openHour, openMinute, closeHour, closeMinute
      if (config.open === 0 && (config.close === 24 || config.close === 0)) {
        open = `${Vue.prototype.$vuetify.t('$vuetify.hours.twentyFourHours')}`
      } else if (config.open !== 0 && (config.close === 24 || config.close === 0)) {
        openHour = moment.duration(config.open, 'hours').get('hours')
        openMinute = moment.duration(config.open, 'hours').get('minutes')
        open = moment({ hour: openHour, minute: openMinute }).format('hh:mm A')
        close = `${Vue.prototype.$vuetify.t('$vuetify.hours.midNight')}`
      } else {
        openHour = moment.duration(config.open, 'hours').get('hours')
        openMinute = moment.duration(config.open, 'hours').get('minutes')
        open = moment({ hour: openHour, minute: openMinute }).format('hh:mm A')

        closeHour = moment.duration(config.close, 'hours').get('hours')
        closeMinute = moment.duration(config.close, 'hours').get('minutes')
        close = moment({ hour: closeHour, minute: closeMinute }).format('hh:mm A')
      }

      const dayInt = config.day

      times.push({ day, open, close, dayInt })
    } else {
      open = `${Vue.prototype.$vuetify.t('$vuetify.common.closed')}`
      times.push({ day, open, close, dayInt: dayKey })
    }
  }

  return orderBy(times, 'dayInt')
}

export const getStoreOpenAndClose = (store, day) => {
  let currentDay
  const hours = get(store, `orderStoreHours`, [])
  const storeTz = get(store, 'timeZone', null) || get(store, 'timezone', null)

  // Default to "now", but in the STORE's timezone, not the browser's — a
  // customer in Vancouver checking a Toronto store's hours must be compared
  // against Toronto's clock, not their own (see nowInStoreTz).
  if (day === undefined) day = nowInStoreTz(storeTz)

  // Ensure we have a day object to work with
  if (typeof day.getTime != 'function')
    day = day != 'today' ? new Date(day) : nowInStoreTz(storeTz)

  if (typeof day !== 'undefined' && typeof day.getDay === 'function') {
    currentDay = day.getDay()
    return find(hours, { day: currentDay + 1 }) || {}
  } else {
    console.error('Could not build Day object: ' + day);
    return {}
  }
}

export const isBeforeOpen = (store) => {
  let { open } = getStoreOpenAndClose(store)

  const now = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))
  const nowHours = now.getHours()
  const nowMinutes = now.getMinutes()
  const nowFraction = getTimeFraction(nowHours, nowMinutes)

  return nowFraction < open
}

export const isAfterClose = (store) => {
  let { open, close } = getStoreOpenAndClose(store)

  if (close <= open) {
    close += 24
  }

  const now = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))
  const nowHours = now.getHours()
  const nowMinutes = now.getMinutes()
  const nowFraction = getTimeFraction(nowHours, nowMinutes)

  return nowFraction > close
}

export const getNextBusinessDay = (store, day) => {
  let date = day

  // All of the below seems like just a waste of processing power
  let { open, close } = getStoreOpenAndClose(store, date)

  if (close < open || isBeforeOpen(store)) {
    // store opens later today
    return date
  } else {
    return date
  }
}

export const getStoreOpenAndCloseNextBusinessDay = (store, dow) => {
  let nextBusinessDay = getNextBusinessDay(store)
  let day = ''
  if(!dow){
    day = nextBusinessDay.getDay()
  } else {
    day = dow
  }

  const hours = get(store, `orderStoreHours`, [])


  return find(hours, { day: day + 1 }) || {}
}

export const determineIfStoreIsClosed = (open, close, dt = new Date()) => {
  const currentHour = dt.getHours()
  const currentMinute = dt.getMinutes()
  const timeFraction = getTimeFraction(currentHour, currentMinute)

  // Assume closed if the values are not defined
  if (open === undefined && close === undefined) {
    return true
  }

  // We are given 2 cases
  const isOpen24Hours = (open === 0 && close === 0) || (open === 0 && close === 24)

  const isCloseLessThanOpen = close < open

  if (isCloseLessThanOpen) {
    return !isOpen24Hours && !(timeFraction >= open && timeFraction < close+24)
    // return !isOpen24Hours && (timeFraction < open && timeFraction >= close)
  } else {
    return !isOpen24Hours && (timeFraction < open || timeFraction >= close)
  }
}

export const extractInvalidRulesFromCoupon = (coupon) => {
  const ruleResults = get(coupon, 'ruleResult.ruleResults', []) || []

  return ruleResults.filter(rule => rule.isEligible === false)
}

export const isCouponValid = (coupon) => {
  return get(coupon, 'ruleResult.isEligible') === true
}

export const isWeChat = () => {
  const ua = navigator.userAgent.toLowerCase()

  return ua.match(/MicroMessenger/i) == 'micromessenger'
}

export const generateUUID = () => uuidv4()

export const ensureDateObject = (toConvert = new Date()) => {
  if (toConvert) {
    // Determine if we recieved a Date object or just string/epoch
    if (typeof toConvert.getTime == 'function') {
      // Is already a Date Object. Nothing needs to be done
      return toConvert
    } else if (typeof toConvert == 'string' || typeof toConvert == 'number') {
      return toConvert == 'today' ? new Date() : new Date(toConvert)
    } else {
      console.error('Cannot convert Date', toConvert);
      return null
    }
  } else {
    return null
  }
}

export const parseDeliveryAddress = (payload, addressData, placeResultData, unitNumber=null) => {
  const matchKeyWord = (keyword = 'locality') => {
    for (let addrCmp of placeResultData.address_components) {
      for (let type of addrCmp.types) {
        if (type.includes(keyword)) {
          return addrCmp.long_name
        }
      }
    }
  }

  // prioritize the addressData object, but use resultData as fall back if match can't be made
  const fetchKeyword = (keyword) => get(addressData, keyword) || matchKeyWord(keyword)

  // Older customers may have their address in state already. If this is the case we need to force it to be reparsed
  if (!payload || 'route' in payload || 'administrative_area_level_1' in payload) {
    return {
      addressLine1: fetchKeyword('name'),
      addressLine2: '',
      city:       fetchKeyword('locality'),
      province:   fetchKeyword('administrative_area_level_1'),
      country:    fetchKeyword('country'),
      postCode :  fetchKeyword('postal_code'),
      unit:       unitNumber,
      gpsCoordinate: {
        latitude: fetchKeyword('latitude'),
        longitude: fetchKeyword('longitude')
      }
    }
  } else {
    return {
      ...payload,
      unit: unitNumber
    }
  }
}

// For some reason pulling query params via $router proved unreliable so this worked better
export const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};


export const matchTarget = (target, menus) => {
  if (!menus || !menus.length) {
    console.log('no menus');
    return []
  }

  let list = []
  const buildCode = (cat, item) => `${item.menuCode}_categories_${cat.code}_items_${item.code}`

  if (target.targetType === 'CATEGORY') {
    for (const menu of menus) {
      // Find the target category and display all of it's items
      const menuItems = menu.menuGroups.filter(cat => target.targetCodes.includes(cat.code)).map(menu => menu.menuItems).flat()
      list = [...list, ...menuItems]
    }
  } else if (target.targetType === 'ITEM') {
    for (const menu of menus) {
      // Search each category for the target item
      for (const cat of menu.menuGroups) {
        list = [
          ...list,
          ...cat.menuItems.filter(item => target.targetCodes.includes(item.code))
        ]
      }
    }
  } else if (target.targetType === 'TAG') {
    for (const menu of menus) {
      // Search each category for the target item
      for (const cat of menu.menuGroups) {
        for (const code of target.targetCodes) {
          list = [
            ...list,
            ...cat.menuItems.filter(item => item.groupingTags && item.groupingTags.includes(code))
          ]
        }
      }
    }
  } else if (target.targetType === 'CATALOG') {
    const { menuGroups } = menus.find(menu => target.targetCodes.includes(menu.code)) || {}

    for (const cat of menuGroups)
      list = [...list, ...cat.menuItems]
  }

  return list
}

export const lookupCode = (itemCode, menus = Vue.prototype.$store.getters.getAllMenus) => {
  if (isNullOrEmpty(menus))
    return null

  for (const menu of menus)
    for (const cat of menu.menuGroups)
      for (const menuItem of cat.menuItems)
        if (itemCode == menuItem.code)
          return menuItem;
  return null
}

export const matchTranslation = (translations, value='value') => {
  if (!translations)
    return null

  let lang = Vue.prototype.$vuetify.lang.current
  // vuetify uses its own lang codes, map back to API lang codes
  if (lang === 'zhHans')
    lang = 'zh'
  else if (lang === 'zhTW')
    lang = 'zh-TW'

  // Look for the currently set translation
  // Fallback to EN if it has a non-empty value, then to the first translation
  const currentTranslation = translations.find(translation => translation.lang == lang)
  if (currentTranslation) return currentTranslation[value]

  const enTranslation = translations.find(translation => translation.lang === 'en')
  if (enTranslation && enTranslation[value]) return enTranslation[value]

  return get(translations, `[0].${value}`, null)
}


export const checkLocation = async () => {
  const { getters, dispatch } = Vue.prototype.$store

  const checkLocPromise = new Promise((resolve, reject) => {
    let storeLocation, options, target
    const dineInRadiusKM  = getters.getDineInRadiusKM

    options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(success, error, options)

    target = {
      latitude:  getters.getStore.gpsCoordinate.latitude,
      longitude:  getters.getStore.gpsCoordinate.longitude
    }

    function success (pos) {
      let crd = pos.coords
      distance(target.latitude, target.longitude, crd.latitude,crd.longitude)
    }

    function error (err) {
      if(err.code === 1){
        dispatch('setInStore', {
          location: true,
          inStore: false,
          inStoreDlg: false
        })
      }
    }

    function distance(lat1, lon1, lat2, lon2) {
      let R = 6371
      let dLat = (lat2-lat1) * Math.PI / 180;
      let dLon = (lon2-lon1) * Math.PI / 180;
      let a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180 ) * Math.cos(lat2 * Math.PI / 180 ) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      let dist = R * c;

      if (dist >= 0 && dist <= dineInRadiusKM) {
        navigator.geolocation.clearWatch(storeLocation)
        dispatch('setInStore', {
          location: false,
          inStore: true,
          inStoreDlg: false
        })
        resolve(true)
      } else {
        navigator.geolocation.clearWatch(storeLocation)
        dispatch('setInStore', {
          location: false,
          inStore: false,
          inStoreDlg: true
        })
        resolve(true)
      }
    }
  })

  return checkLocPromise
}

// DEV-11531: backend messages like ORD_ERR_0006 "{0} is out of stock" / "{0} 现缺货" have no ending
// punctuation; end them as a sentence ("。" after CJK text) before showing them to guests.
export const endSentence = (text) => {
  if (typeof text !== 'string' || !text.trim()) return text
  const trimmed = text.trim()
  if (/[.!?。！？]$/.test(trimmed)) return trimmed
  return trimmed + (/[\u3000-\u9fff\uff00-\uffef]$/.test(trimmed) ? '。' : '.')
}

export const findErrorMessage = (error) => {
  if (typeof error == 'string')
    return error

  const errorMsg = get(error, 'res.data.errorMsg') || get(error, 'response.data.errorMsg') || error.request || error.message
  const errorCode = get(error, 'res.data.errorCode') || get(error, 'response.data.errorCode')
  return errorMsg || errorCode || error
}


export const isNullOrEmpty = (list) => list == null || list.length == 0 || Object.keys(list).length == 0

export const isNull = (item) => item == null || item == '' || isNullOrEmpty(item)


export const disableDocumentScrolling = () => {
  const style = `
    position: relative;
    overflow: hidden;
    scroll-behavior: auto;
  `

  document.getElementsByTagName("body")[0].style = style
  document.getElementsByTagName("html")[0].style = style;
  document.getElementById("app").style = style;
}

export const enableDocumentScrolling = (scrollTop, scrollToMenu = false) => {

  if (!!scrollTop) {
    document.getElementsByTagName("body")[0].style = 'scroll-behavior: auto;'
    document.getElementsByTagName("html")[0].style = 'scroll-behavior: auto;';
    document.getElementById("app").style = 'scroll-behavior: auto;';
              
    document.getElementsByTagName("body")[0].scrollTop = scrollTop
    document.getElementsByTagName("html")[0].scrollTop = scrollTop
    document.getElementById("app").scrollTop = scrollTop
  }
  
  // Always remove scrolling manipulation to be safe
  document.getElementsByTagName("body")[0].style = ''
  document.getElementsByTagName("html")[0].style = '';
  document.getElementById("app").style = '';

  if (scrollToMenu) {
    const observer = new MutationObserver(() => {
      EventBus.$emit('scrollToMenu');
      observer.disconnect();
    });
    observer.observe(document.getElementById('app'), { childList: true, subtree: true });
  }
}

// Builds the target Date to use for schedule checks based on checkout info.
// When a scheduledDay is selected, combines it with the scheduledTime.
// Falls back to new Date() (current time) when no scheduled day is set.
export function buildScheduledTargetDate(checkoutInfo) {
  const scheduledDay = checkoutInfo && checkoutInfo.scheduledDay || null;
  const scheduledTime = checkoutInfo && checkoutInfo.scheduledTime || null;
  if (scheduledDay) {
    const targetDate = new Date(scheduledDay);
    if (scheduledTime === 'now') {
      const now = new Date();
      targetDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    } else if (scheduledTime && !isNaN(Date.parse(scheduledTime))) {
      const parsedTime = new Date(scheduledTime);
      targetDate.setHours(parsedTime.getHours(), parsedTime.getMinutes(), parsedTime.getSeconds());
    }
    return targetDate;
  }
  return new Date();
}

// Finds the last index in a composite queue matching a working item.
// Prefers exact _instanceId match; falls back to last code+relationName match
// when the item's _instanceId isn't yet reflected in the rebuilt queue (DEV-8545).
export function findLastIndexInCompositeQueue(arr, workingItem) {
  const { relationName, code, _instanceId } = workingItem
  let lastIndex = -1;
  let lastCodeMatch = -1;
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    if (item && _instanceId && item._instanceId === _instanceId) {
      lastIndex = i;
    } else if (item && item.relationName === relationName && item.code === code && !_instanceId) {
      lastIndex = i;
    }
    if (item && item.code === code && item.relationName === relationName) {
      lastCodeMatch = i;
    }
  }
  return lastIndex >= 0 ? lastIndex : lastCodeMatch;
}

// DEV-11178: a combo child's composite-queue index, worked out from its SLOT (which relation
// it's in + its position in that relation's selectedItem) instead of searched for. The queue
// is [parent, ...each relation's maxCardinality slots, in relation order] -- the same layout
// openSelection, mounted and buildMenuStructure all use. Used when a search misses, in place
// of "last index"/"append", which put the slot-1 item into the last slot (order #4690).
// last: the instance's last qty slot instead of its first. Returns -1 if the slot isn't found.
export function compositeSlotIndex(parentItem, child, last = false) {
  const relations = (parentItem && parentItem.relations) || []
  if (!child) return -1
  let offset = 1
  for (const relation of relations) {
    const selected = relation.selectedItem || []
    if (relation.name === child.relationName || relation.code === child.relationName) {
      let position = 0
      for (const item of selected) {
        const qty = item.qty || 1
        const isChild = child._instanceId ? item._instanceId === child._instanceId : item.code === child.code
        if (isChild) return offset + position + (last ? qty - 1 : 0)
        position += qty
      }
      return -1
    }
    offset += relation.maxCardinality > 0 ? relation.maxCardinality : selected.length
  }
  return -1
}

// Formats a delivery address object into a human-readable string.
export function formatGroupOrderAddress(address) {
  if (!address || typeof address !== 'object') return '';
  const { addressLine1, city, province, postCode, country } = address;
  return [addressLine1, city, province, postCode, country].filter(Boolean).join(', ');
}
